/**
 * Project persistence (brief 4.5): projects live in IndexedDB in the
 * user's browser. Nothing here transmits anything; "Delete all local
 * data" wipes the database (project deletion and the data-retention
 * toggle are store actions built on this interface).
 *
 * Serialization is JSON with a codec for non-finite numbers: engine
 * results legitimately contain Infinity (a CV for a zero estimate)
 * and JSON would silently turn it into null.
 */

import { openDB } from 'idb';
import type { IDBPDatabase } from 'idb';

import type { LicensePass } from '../domain/license.js';
import type { ProjectState } from '../domain/types.js';

export interface ProjectStorage {
  saveProject(state: ProjectState): Promise<void>;
  loadProject(): Promise<ProjectState | null>;
  saveLicense(pass: LicensePass | null): Promise<void>;
  loadLicense(): Promise<LicensePass | null>;
  deleteAll(): Promise<void>;
}

const NONFINITE = '$nonfinite';

function replacer(_key: string, value: unknown): unknown {
  if (typeof value === 'number' && !Number.isFinite(value)) {
    return { [NONFINITE]: Number.isNaN(value) ? 'NaN' : value > 0 ? 'Infinity' : '-Infinity' };
  }
  return value;
}

function reviver(_key: string, value: unknown): unknown {
  if (
    value !== null &&
    typeof value === 'object' &&
    NONFINITE in (value as Record<string, unknown>)
  ) {
    const tag = (value as Record<string, unknown>)[NONFINITE];
    if (tag === 'Infinity') return Infinity;
    if (tag === '-Infinity') return -Infinity;
    if (tag === 'NaN') return NaN;
  }
  return value;
}

export function serializeProject(state: ProjectState): string {
  return JSON.stringify(state, replacer);
}

export function deserializeProject(text: string): ProjectState {
  return JSON.parse(text, reviver) as ProjectState;
}

export function serializeLicense(pass: LicensePass): string {
  return JSON.stringify(pass);
}

/**
 * The snapshot actually written: a run in flight is persisted as
 * idle (a reloaded app is not running), and when the user turns off
 * "keep the data file with the project", the row data is dropped
 * while every decision, setting, and result is kept — restoring
 * then asks for the file again instead of pretending it is there.
 */
export function snapshotForStorage(state: ProjectState): ProjectState {
  let out = state;
  if (out.run.status === 'running') {
    out = {
      ...out,
      run: { ...out.run, status: 'idle', progress: [] },
    };
  }
  if (!out.keepDataWithProject && out.source.table !== null) {
    out = {
      ...out,
      source: {
        ...out.source,
        table: { ...out.source.table, rows: [] },
        tableMissing: true,
      },
      covariatesSource:
        out.covariatesSource !== null && out.covariatesSource.table !== null
          ? {
              ...out.covariatesSource,
              table: { ...out.covariatesSource.table, rows: [] },
            }
          : out.covariatesSource,
    };
  }
  return out;
}

/** In-memory storage with the same serialization path (tests). */
export class MemoryProjectStorage implements ProjectStorage {
  #project: string | null = null;
  #license: string | null = null;

  async saveProject(state: ProjectState): Promise<void> {
    this.#project = serializeProject(snapshotForStorage(state));
  }
  async loadProject(): Promise<ProjectState | null> {
    return this.#project === null ? null : deserializeProject(this.#project);
  }
  async saveLicense(pass: LicensePass | null): Promise<void> {
    this.#license = pass === null ? null : serializeLicense(pass);
  }
  async loadLicense(): Promise<LicensePass | null> {
    return this.#license === null
      ? null
      : (JSON.parse(this.#license) as LicensePass);
  }
  async deleteAll(): Promise<void> {
    this.#project = null;
    this.#license = null;
  }
}

const DB_NAME = 'sae-studio';
const STORE = 'kv';
const PROJECT_KEY = 'project';
const LICENSE_KEY = 'license';

/** IndexedDB storage: the browser build's ProjectStorage. */
export class IdbProjectStorage implements ProjectStorage {
  #db: Promise<IDBPDatabase> | null = null;

  #open(): Promise<IDBPDatabase> {
    if (this.#db === null) {
      this.#db = openDB(DB_NAME, 1, {
        upgrade(db) {
          if (!db.objectStoreNames.contains(STORE)) {
            db.createObjectStore(STORE);
          }
        },
      });
    }
    return this.#db;
  }

  async saveProject(state: ProjectState): Promise<void> {
    const db = await this.#open();
    await db.put(STORE, serializeProject(snapshotForStorage(state)), PROJECT_KEY);
  }
  async loadProject(): Promise<ProjectState | null> {
    const db = await this.#open();
    const text = (await db.get(STORE, PROJECT_KEY)) as string | undefined;
    return text === undefined ? null : deserializeProject(text);
  }
  async saveLicense(pass: LicensePass | null): Promise<void> {
    const db = await this.#open();
    if (pass === null) await db.delete(STORE, LICENSE_KEY);
    else await db.put(STORE, serializeLicense(pass), LICENSE_KEY);
  }
  async loadLicense(): Promise<LicensePass | null> {
    const db = await this.#open();
    const text = (await db.get(STORE, LICENSE_KEY)) as string | undefined;
    return text === undefined ? null : (JSON.parse(text) as LicensePass);
  }
  async deleteAll(): Promise<void> {
    const db = await this.#open();
    await db.clear(STORE);
  }
}
